/**
 *
 * ReleaseOverviewActionBar
 *
 */

import React, { useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage } from 'lib/intl';

// icons
import { AdjustView, DoneAllIcon } from 'lib/domonda-icons';

// ui
import {
  createStyles,
  withStyles,
  WithStyles,
  iconButtonClassName,
  Menu,
  MenuItem,
  Button,
} from '@domonda/ui';

// parts
import { useReleaseOverviewQueryParams, ReleaseOverviewQueryParams } from './queryParams';

const styles = createStyles(({ palette }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    borderBottom: `1px solid ${palette.border}`,
    borderTop: `1px solid ${palette.border}`,
    height: '100%',
  },
  actions: {
    display: 'flex',
    padding: '6px 18px 6px 18px',
    zIndex: 3,
    [`& .${iconButtonClassName}`]: {
      marginRight: 8,
      '&:last-child': {
        marginRight: 0,
      },
    },
    '& + &': {
      borderLeft: `1px solid ${palette.border}`,
    },
    '&:last-child': {
      paddingRight: 0,
    },
  },
}));

function deriveStateName(documentState: ReleaseOverviewQueryParams['documentState']) {
  switch (documentState) {
    case 'validated':
      return <FormattedMessage id="DOCUMENTS_VALIDATED" />;
    case 'autoprocessed':
      return <FormattedMessage id="DOCUMENTS_AUTOPROCESSED" />;
  }
}

export interface ReleaseOverviewActionBarProps {
  selectedInvoiceIds: UUID[];
  onRelease: (invoiceIds: UUID[] | undefined) => void;
}

const ReleaseOverviewActionBar: React.FC<
  ReleaseOverviewActionBarProps & WithStyles<typeof styles>
> = (props) => {
  const { classes, selectedInvoiceIds, onRelease } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [queryParams, setQueryParams] = useReleaseOverviewQueryParams();

  return (
    <div className={clsx(classes.root)}>
      <div className={classes.actions}>
        <Button variant="contained" onClick={(event) => setAnchorEl(event.currentTarget)}>
          <AdjustView />
          <span>
            <FormattedMessage id="DOCUMENTS_STATE" />
            :&nbsp;{deriveStateName(queryParams.documentState)}
          </span>
        </Button>

        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem
            disabled={queryParams.documentState === 'validated'}
            onClick={() => {
              setQueryParams({ ...queryParams, documentState: 'validated' });
              setAnchorEl(null);
            }}
          >
            {deriveStateName('validated')}
          </MenuItem>

          <MenuItem
            disabled={queryParams.documentState === 'autoprocessed'}
            onClick={() => {
              setQueryParams({ ...queryParams, documentState: 'autoprocessed' });
              setAnchorEl(null);
            }}
          >
            {deriveStateName('autoprocessed')}
          </MenuItem>
        </Menu>
      </div>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onRelease(selectedInvoiceIds.length > 0 ? selectedInvoiceIds : undefined)}
        >
          <DoneAllIcon />
          <span>
            {selectedInvoiceIds.length > 0 ? (
              <FormattedMessage
                id="RELEASE_INVOICES"
                values={{
                  // eslint-disable-next-line react/display-name
                  strong: (msg: string) => <strong>{msg}</strong>,
                  count: selectedInvoiceIds.length,
                }}
              />
            ) : (
              <FormattedMessage
                id="RELEASE_ALL"
                values={{
                  // eslint-disable-next-line react/display-name
                  strong: (msg: string) => <strong>{msg}</strong>,
                }}
              />
            )}
          </span>
        </Button>
      </div>
    </div>
  );
};

const StyledReleaseOverviewActionBar = withStyles(styles)(ReleaseOverviewActionBar);
export { StyledReleaseOverviewActionBar as ReleaseOverviewActionBar };
